import { Color, Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import styled, { css } from 'styled-components';

import { GraphQuery, GraphQueryStatus } from 'dashboard/models/graph-query';
import { isGraphQueryTemplateOption, StateAndContext } from 'dashboard/modules/GraphV2/types';
import { Paragraph } from 'design-system/components/Text';
import {
  DATE_AND_TIME_NO_YEAR_TZ_FORMAT,
  DATE_AND_TIME_TZ_FORMAT,
} from 'lib/constants/luxonFormats';

const CurrentViewMessageContainer = styled.div`
  margin-top: auto;
`;

const SelectGraphQueryContainer = styled.div`
  margin-top: auto;
  border-radius: ${Spacing[1]};
  padding: ${Spacing[1]};
  gap: ${Spacing[2]};
  background-color: ${Color.CoreSlate100};
  display: flex;
`;

const SelectGraphQueryItem = styled.div<{ selected: boolean }>`
  border-radius: ${Spacing[1]};
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
  cursor: pointer;
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Color.PrimaryWhite};
    `}
`;

const StyledParagraph = styled(Paragraph)<{ disabled: boolean; hasFailed: boolean }>`
  ${({ theme, disabled, hasFailed }) => css`
    color: ${disabled
      ? theme.color.text.disabled
      : hasFailed
        ? Color.CoreRed900
        : theme.color.text.primary};
  `}
`;

interface NavigationHeaderGraphQueryOptionProps {
  label: string;
  graphQuery: GraphQuery;
  onSwitch: () => void;
  selected: boolean;
  disabled: boolean;
  hasFailed: boolean;
}

const NavigationHeaderGraphQueryOption = ({
  label,
  graphQuery,
  onSwitch,
  selected,
  disabled,
  hasFailed,
}: NavigationHeaderGraphQueryOptionProps) => {
  const createdAt = graphQuery.attributes['created-at'];

  return (
    <SelectGraphQueryItem
      id='graph_cases_card__time_select_tab'
      selected={selected}
      onClick={() => {
        if (!selected && !disabled) {
          onSwitch();
        }
      }}
    >
      <StyledParagraph disabled={disabled} hasFailed={hasFailed}>
        {DateTime.fromISO(createdAt).toFormat(
          DateTime.fromISO(createdAt).year === DateTime.local().year
            ? DATE_AND_TIME_NO_YEAR_TZ_FORMAT
            : DATE_AND_TIME_TZ_FORMAT,
        )}
        {` (${label})`}
      </StyledParagraph>
    </SelectGraphQueryItem>
  );
};

interface NavigationHeaderProps {
  stateAndContext: StateAndContext;
  onSwitch: (viewBaseQuery: boolean) => void;
}

const NavigationHeader = ({ stateAndContext, onSwitch }: NavigationHeaderProps) => {
  const {
    graphQueryToGraphQueryInfo,
    baseGraphQueryToCurrentGraphQuery,
    graphQueryOptionIdToGraphQuery,
    selectedGraphQueryOptionId,
    viewBaseQuery,
  } = stateAndContext;

  const baseGraphQuery = graphQueryOptionIdToGraphQuery.current[selectedGraphQueryOptionId];
  if (baseGraphQuery == null) {
    return null;
  }

  // graph query template
  if (isGraphQueryTemplateOption(selectedGraphQueryOptionId)) {
    const createdAt = baseGraphQuery.attributes['created-at'];

    return (
      <CurrentViewMessageContainer>
        <Paragraph color='secondary'>
          Query results from{' '}
          {DateTime.fromISO(createdAt).toFormat(
            DateTime.fromISO(createdAt).year === DateTime.local().year
              ? DATE_AND_TIME_NO_YEAR_TZ_FORMAT
              : DATE_AND_TIME_TZ_FORMAT,
          )}
        </Paragraph>
      </CurrentViewMessageContainer>
    );
  }
  // attached Graph Query
  else {
    // if the currentGraphQuery has not even been submitted, just hide the nav bar
    const currentGraphQuery = baseGraphQueryToCurrentGraphQuery.current[baseGraphQuery.id];
    if (currentGraphQuery == null) {
      return null;
    }

    // we can switch to the current graph query if query is completed and data is hydrated. That is when the GraphQueryInfo is generated.
    const currentGraphQueryInfo = graphQueryToGraphQueryInfo.current[currentGraphQuery?.id];
    const isCurrentGraphQueryDisabled = currentGraphQueryInfo == null;
    const isCurrentGraphQuerySelected = !isCurrentGraphQueryDisabled && !viewBaseQuery;
    const hasCurrentQueryFailed = [GraphQueryStatus.Errored, GraphQueryStatus.TimedOut].includes(
      currentGraphQueryInfo?.graphQuery?.attributes?.status,
    );

    const baseGraphQueryInfo = graphQueryToGraphQueryInfo.current[baseGraphQuery?.id];
    const isBaseQueryDisabled = baseGraphQueryInfo == null;
    const isBaseQuerySelected = !isBaseQueryDisabled && viewBaseQuery;
    const hasBaseQueryFailed = [GraphQueryStatus.Errored, GraphQueryStatus.TimedOut].includes(
      baseGraphQueryInfo?.graphQuery?.attributes?.status,
    );

    return (
      <SelectGraphQueryContainer>
        <NavigationHeaderGraphQueryOption
          graphQuery={baseGraphQuery}
          onSwitch={() => {
            onSwitch(true);
          }}
          selected={isBaseQuerySelected}
          disabled={isBaseQueryDisabled}
          hasFailed={hasBaseQueryFailed}
          label='Case Creation'
        />
        <NavigationHeaderGraphQueryOption
          graphQuery={currentGraphQuery}
          onSwitch={() => {
            onSwitch(false);
          }}
          selected={isCurrentGraphQuerySelected}
          disabled={isCurrentGraphQueryDisabled}
          hasFailed={hasCurrentQueryFailed}
          label='Present'
        />
      </SelectGraphQueryContainer>
    );
  }
};

export default NavigationHeader;
